import React from "react";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import domtoimage from "dom-to-image";
import { saveAs } from "file-saver";
import chroma from "chroma-js";

import {
  Avatar,
  Box,
  IconButton,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Typography
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";

import SaveIcon from "@material-ui/icons/Save";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import "typeface-open-sans";

import RoomComparisonCardHeader from "./RoomCard/RoomComparisonCardHeader";

//
// RoomCardMultiGraph
//

class UnstyledRoomCardMultiGraph extends React.Component {
  static styles() {
    return {
      Container: {
        width: "100%",
        height: "25vw",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "1em"
      },

      RechartsContainer: {
        backgroundColor: "#F8F9FC"
      },

      XAxis: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
      }
    };
  }

  render() {
    const classes = this.props.classes;

    if (this.props.loading) {
      return (
        <Box className={classes.Container}>
          <CircularProgress className={classes.Progress} />
        </Box>
      );
    }

    // Make me more efficient
    let data = [];
    this.props.rooms.forEach(room => {
      room.orderedReadings().forEach(m => {
        data.push(
          new Map(
            Object.entries({
              id: m.id,
              timestamp: Number(moment(m.timestamp).valueOf()),
              [room.id]: Number(m.payload.ppm.toFixed(1))
            })
          )
        );
      });
    });
    data = data.sort((a, b) => a.get("timestamp") - b.get("timestamp"));

    let graph;
    if (this.props.rooms.length < 1) {
      graph = <Typography>No rooms selected.</Typography>;
    } else {
      let months = [];
      if (data.length > 0) {
        const firstMonthTick = moment(data[0].get("timestamp"))
          .startOf("month")
          .add(1, "months");
        
          const lastMonthTick = moment(
          data[data.length - 1].get("timestamp")
        ).startOf("month");

        // months_diff check prevents <XAxis /> app crash when difference is 0
        // see: https://github.com/StrellaBiotech/dashboard-frontend-legacy/issues/62
        const monthsDiff = lastMonthTick.diff(firstMonthTick, "months") + 1;
        if(monthsDiff > 0) {
          months = new Array(monthsDiff)
          .fill()
          .map((_, i) => firstMonthTick.clone().add(i, "months"));
        }
        else {
          months = [firstMonthTick];
        }
      }
      graph = (
        <>
          <ResponsiveContainer
            className={classes.RechartsContainer}
            width={"100%"}
          >
            <LineChart data={data}>
              <CartesianGrid
                vertical={false}
                horizontalPoints={[
                  25,
                  50,
                  75,
                  100,
                  125,
                  150,
                  175,
                  200,
                  225,
                  250,
                  275,
                  300,
                  325,
                  350,
                  375,
                  400
                ]}
              />
              <XAxis
                axisLine={false}
                dy={15}
                tickLine={false}
                ticks={months}
                tickFormatter={value => value.format("MMM")}
                type={"number"}
                dataKey={measurement => measurement.get("timestamp")}
                domain={["dataMin", "dataMax"]}
              />
              <YAxis hide={true} type={"number"} domain={[-5, 95]} />
              {this.props.rooms.map(r => (
                <Line
                  key={r.id}
                  type={"monotone"}
                  dataKey={measurement => measurement.get(r.id)}
                  isAnimationActive={false}
                  dot={false}
                  stroke={this.props.colors[r.id].hex()}
                  strokeWidth={4}
                  connectNulls={true}
                />
              ))}
              <Tooltip
                labelFormatter={value => moment(value).format("LLLL")}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      );
    }

    return <Box className={classes.Container}>{graph}</Box>;
  }
}
const RoomCardMultiGraph = withStyles(UnstyledRoomCardMultiGraph.styles)(
  observer(UnstyledRoomCardMultiGraph)
);

// RoomCardGraph

const ColoredCheckbox = color =>
  withStyles({
    root: {
      color: color.hex()
    }
  })((props) => <Checkbox color="default" {...props} />);

const styles = theme => ({
  RoomComparisonCard: {
    width: "100%",
    height: "25vw"
  },

  SelectionBox: {
    overflowY: "auto",
    padding: theme.spacing(1)
  },

  VarietyBox: {
    width: "100%",
    padding: "0rem 1rem",
    marginTop: "1rem"
  },

  Variety: {
    fontSize: "1rem",
    lineHeight: "1.25rem",
    fontWeight: "500",
    color: "#0A0E34"
  },

  NotesBox: {
    marginTop: "1rem",
    marginBottom: "1rem",
    padding: "0rem 1rem"
  },

  GraphContainer: {
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)"
  },

  LargeRoomListCard: {
    width: theme.spacing(62),
    height: theme.spacing(28)
  },
  RoomListCard: {
    width: theme.spacing(30),
    height: theme.spacing(28)
  },
  RoomListCardHeader: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  },
  ViewSwitcher: {
    display: "flex",
    flexDirection: "row"
  },
  ViewLabel: {
    cursor: "pointer",
    color: "rgba(0, 0, 0, 0.5)"
  },
  ActiveViewLabel: {
    fontWeight: 500,
    color: "rgba(0, 0, 0, 1.0)",
    borderBottom: ".1rem solid black"
  },
  RoomListCardBody: {
    padding: theme.spacing(1.5),
    paddingTop: 0,
    paddingBottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "space-between",
    justifyContent: "space-between"
  },

  SmallStatLabel: {
    color: "rgba(0, 0, 0, 0.5)",
    spacing: "2px",
    lineHeight: 1.1
  },
  SmallStatValue: {
    lineHeight: 1.1,
    marginBottom: theme.spacing(1.5)
  },

  RoomListCardStatusStable: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#c2e791"
  },
  RoomListCardStatusMaturing: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#f7cd84"
  },
  RoomListCardStatusMature: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#e6a2aa"
  },
  RoomListCardStatusNone: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#DCDCDC"
  },
  RoomListCardStatusOpen: {
    width: "100%",
    height: "1rem",
    backgroundColor: "#444444"
  },

  progressContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },

  SelectionsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },

  SelectionContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    paddingBottom: "0.25rem"
  }
});

class UnstyledRoomCard extends React.Component {
  constructor(props) {
    super(props);
    this.downloadRef = React.createRef();
    this.downloadIconRef = React.createRef();
    this.state = {
      selected: []
    };
  }

  setSelected(which) {
    return checked => {
      this.setState(state => ({
        ...state,
        selected: checked
          ? state.selected.indexOf(which) !== -1
            ? state.selected
            : state.selected.concat(which)
          : state.selected.filter(id => id !== which)
      }));
    };
  }

  addSelected(added) {
    this.setState(state => ({
      ...state,
      selected: state.selected.concat(added)
    }));
  }

  removeSelected(removed) {
    this.setState(state => ({
      ...state,
      selected: state.selected.filter(id => id !== removed)
    }));
  }

  downloadGraphAsPng() {
    domtoimage.toPng(this.downloadRef.current).then(dataUrl => {
      saveAs(dataUrl, `RoomComparison.png`);
    });
  }

  render() {
    const classes = this.props.classes;

    const palette = []
      .concat(
        ...new Array(this.props.rooms.length).fill([
          chroma("#3C3DEB"),
          chroma("#239E9D"),
          chroma("#8A9E23"),
          chroma("#EB5727"),
          chroma("#A3207E")
        ])
      )
      .slice(0, this.props.rooms.length);
    let paletteMap = new Map();
    this.props.rooms.forEach((r, i) => {
      paletteMap[r.id] = palette[i];
    });

    let selections = <Typography>No rooms available.</Typography>;
    if (this.props.rooms.length !== 0) {
      selections = (
        <>
          <Grid container className={classes.SelectionsContainer} style={{ flexWrap: "nowrap" }}>
            {this.props.rooms
              .filter((r) => this.state.selected.indexOf(r.id) !== -1)
              .sort((a, b) => {
                if (
                  a.predicted_ready_timestamp != null &&
                  b.predicted_ready_timestamp != null
                ) {
                  return a.predicted_ready_timestamp.localeCompare(
                    b.predicted_ready_timestamp
                  );
                } else if (
                  a.predicted_ready_timestamp == null &&
                  b.predicted_ready_timestamp == null
                ) {
                  return a.human_name.localeCompare(b.human_name);
                } else {
                  return a.predicted_ready_timestamp != null ? -1 : 1;
                }
              })
              .map((r) => {
                return (
                  <Grid
                    item
                    xs={12}
                    container
                    className={classes.SelectionContainer}
                    key={r.id}
                    style={{ flexWrap: "nowrap" }}
                  >
                    <Grid item>
                      <Avatar
                        style={{
                          backgroundColor: paletteMap[r.id],
                          width: "1rem",
                          height: "1rem",
                          marginRight: "0.5rem",
                          marginLeft: "0.5rem"
                        }}
                      >
                        &nbsp;
                      </Avatar>
                    </Grid>
                    <Grid item>
                      <Typography noWrap>{r.human_name}</Typography>
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
        </>
      );
    }

    return (
      <>
        <Card className={classes.RoomComparisonCard}>
          <Grid container style={{ height: "100%", flexWrap: "nowrap", backgroundColor: "white"}} ref={this.downloadRef}>
            <Grid item style={{ flexGrow: 0, flexBasis: "content" }}>
              <RoomComparisonCardHeader variety={this.props.variety} />
              <Divider />
              <Box className={classes.SelectionBox}>{selections}</Box>
            </Grid>
            <Grid
              item
              xs
              className={classes.GraphContainer}
              style={{ flexShrink: 1, flexBasis: "content" }}
            >
              <RoomCardMultiGraph
                rooms={this.state.selected.map(
                  id => this.props.rooms.filter(r => r.id === id)[0]
                )}
                colors={paletteMap}
              />
            </Grid>
          </Grid>
        </Card>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ flexWrap: "wrap" }}
        >
        {(this.props.user || {}).is_staff ? (
          <>
            <IconButton
              color="secondary"
              aria-label="Download as .png"
              onClick={() => this.downloadGraphAsPng()}
            >
              <SaveIcon />
            </IconButton>
          </>
        ) : (
          <> </>
        )}
          {this.props.rooms.map((r) => {
            const selected = this.state.selected.indexOf(r.id) !== -1;
            const DistinctCheckbox = ColoredCheckbox(paletteMap[r.id]);

            const variety = r.varieties.length
              ? r.varieties
                  .map(v => v.human_name || "")
                  .filter(n => n !== "")
                  .sort()
                  .join(", ")
              : "";

            return (
              <Grid
                item
                key={r.id}
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                style={{
                  paddingRight: "18px",
                  width: "auto",
                  flex: "0 0 auto",
                  flexWrap: "nowrap"
                }}
              >
                <Grid item>
                  <DistinctCheckbox
                    checked={selected}
                    onChange={e => this.setSelected(r.id)(e.target.checked)}
                  />
                </Grid>
                <Grid item>
                  <Typography>
                    <Box
                      component="span"
                      fontWeight={
                        selected ? "fontWeightBold" : "fontWeightRegular"
                      }
                    >
                      {r.human_name} ({variety})
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  }
}

export default withRouter(
  withStyles(styles)(
    inject(stores => ({
      rootStore: stores.rootStore,
      user: stores.rootStore.authStore.user
    }))(observer(UnstyledRoomCard))
  )
);
